const BOOST_PRODUCT_LIMIT = 10;
const BOOST_SUGGESTION_LIMIT = 4;
const BOOST_COLLECTION_LIMIT = 3;

function escapeHtml(text) {
  const element = document.createElement('div');
  element.textContent = text;
  return element.innerHTML;
}

function escapeRegExp(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function decodeHtmlEntities(text) {
  const element = document.createElement('textarea');
  element.innerHTML = text;
  return element.value;
}

function replaceTranslationPlaceholders(template, replacements) {
  return Object.entries(replacements).reduce(
    (result, [key, value]) => result.replaceAll(key, value),
    template
  );
}

function pluralize(count, strings) {
  const template = count === 1 ? strings.one : strings.other;
  return replaceTranslationPlaceholders(template, { '{{ count }}': String(count) });
}

function buildSearchForText(searchTerm) {
  const template = decodeHtmlEntities(window.searchStrings.searchFor);
  return replaceTranslationPlaceholders(template, {
    '__TERMS__': searchTerm,
  });
}

function getStorefrontPrefix() {
  const searchPath = new URL(window.routes.search_url, window.location.origin).pathname;
  return searchPath.replace(/\/search\/?$/, '');
}

function buildRouteUrl(path) {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getStorefrontPrefix()}${normalizedPath}`;
}

function buildSearchResultsUrl(query) {
  const url = new URL(window.routes.search_url, window.location.origin);
  url.searchParams.set('q', query);
  return `${url.pathname}${url.search}`;
}

function buildProductUrl(handle) {
  return buildRouteUrl(`/products/${handle}`);
}

function buildCollectionUrl(handle) {
  return buildRouteUrl(`/collections/${handle}`);
}

function getProductImage(product) {
  const image = product.images_info?.[0];
  if (!image?.src) return null;

  const width = 50;
  const height = image.width && image.height
    ? Math.round(width / (image.width / image.height))
    : width;
  const separator = image.src.includes('?') ? '&' : '?';

  return {
    src: `${image.src}${separator}width=150`,
    alt: image.alt || product.title,
    width,
    height,
  };
}

async function boostInstantSearch(query, signal) {
  const url = new URL('https://services.mybcapps.com/bc-sf-filter/search/suggest');
  url.searchParams.set('shop', window.searchStrings.shop);
  url.searchParams.set('q', query);
  url.searchParams.set('product_limit', String(BOOST_PRODUCT_LIMIT));
  url.searchParams.set('suggestion_limit', String(BOOST_SUGGESTION_LIMIT));
  url.searchParams.set('collection_limit', String(BOOST_COLLECTION_LIMIT));
  url.searchParams.set('re_run_if_typo', 'true');
  url.searchParams.set('return_all_currency_fields', 'false');

  if (window.searchStrings.locale) {
    url.searchParams.set('locale', window.searchStrings.locale);
  }

  (window.searchStrings.hiddenProductOptions || []).forEach((option) => {
    url.searchParams.append('h_options', option);
  });

  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Boost instant search failed: ${response.status}`);
  }

  return response.json();
}

function buildLiveRegionText(data, searchTerm, products) {
  const strings = window.searchStrings;
  const suggestionsCount = data.suggestions?.length || 0;
  const collectionsCount = data.collections?.length || 0;
  const productsCount = products.length;
  const totalResults = suggestionsCount + collectionsCount + productsCount;

  if (totalResults === 0) {
    return replaceTranslationPlaceholders(decodeHtmlEntities(strings.noResults), {
      '__TERMS__': searchTerm,
    });
  }

  let text = `${pluralize(totalResults, strings.resultsWithCount)}: `;

  if (suggestionsCount > 0) {
    text += `${pluralize(suggestionsCount, strings.resultsSuggestionsWithCount)}, `;
  }

  if (collectionsCount > 0) {
    text += `${collectionsCount} ${strings.collections}, `;
  }

  if (productsCount > 0) {
    text += pluralize(productsCount, strings.resultsProductsWithCount);
  }

  text = text.replace(/, $/, '');

  // Announce the spelling correction too, otherwise screen reader users hear results for a term
  // they didn't type.
  const correctedTerm = getCorrectedTerm(data);
  if (correctedTerm) {
    const correction = replaceTranslationPlaceholders(decodeHtmlEntities(strings.didYouMean || ''), {
      '__TERMS__': correctedTerm,
    });
    return `${correction} ${text}`;
  }

  return text;
}

// Boost returns its spelling correction as did_you_mean, falling back to correct_query.
function getCorrectedTerm(data) {
  return data.did_you_mean?.[0] || data.correct_query || '';
}

// Renders as a selectable row so the arrow keys and Enter reach it like any other result.
function buildDidYouMeanMarkup(data) {
  const term = getCorrectedTerm(data);
  if (!term) return '';

  const strings = window.searchStrings;
  const [before, after] = decodeHtmlEntities(strings.didYouMean || '').split('__TERMS__');

  return `
    <ul id="predictive-search-results-did-you-mean-list" class="predictive-search__results-list mt-8" role="group">
      <li id="predictive-search-option-did-you-mean" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${buildSearchResultsUrl(term)}" class="predictive-search__item link" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading">${escapeHtml(before || '')}<strong>${escapeHtml(term)}</strong>${escapeHtml(after || '')}</p>
          </div>
        </a>
      </li>
    </ul>`;
}

function buildPredictiveSearchMarkup(data) {
  const strings = window.searchStrings;
  const products = data.products || [];
  const suggestions = data.suggestions || [];
  const collections = data.collections || [];
  const hasSuggestions = suggestions.length > 0;
  const hasCollections = collections.length > 0;
  const hasProducts = products.length > 0;

  // Popular suggestions open the full search results page for that term.
  const suggestionItems = suggestions.map((suggestion, index) => `
      <li id="predictive-search-option-query-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${buildSearchResultsUrl(suggestion)}" class="predictive-search__item link" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading">${escapeHtml(suggestion)}</p>
          </div>
        </a>
      </li>`).join('');

  const collectionItems = collections.map((collection, index) => `
      <li id="predictive-search-option-collection-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${buildCollectionUrl(collection.handle)}" class="predictive-search__item link" tabindex="-1">
          <div class="predictive-search__item-content predictive-search__item-content--centered">
            <p class="predictive-search__item-heading">${escapeHtml(collection.title)}</p>
          </div>
        </a>
      </li>`).join('');

  const productItems = products.map((product, index) => {
    const image = getProductImage(product);
    const imageMarkup = image
      ? `<img class="predictive-search__image" src="${escapeHtml(image.src)}" alt="${escapeHtml(image.alt)}" width="${image.width}" height="${image.height}">`
      : '';

    return `
      <li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${buildProductUrl(product.handle)}" class="predictive-search__item predictive-search__item--link-with-thumbnail" tabindex="-1">
          ${imageMarkup}
          <div class="predictive-search__item-content">
            <p>${escapeHtml(product.title)}</p>
          </div>
        </a>
      </li>`;
  }).join('');

  const resultGroupMarkup = (hasSuggestions || hasCollections || hasProducts)
    ? `<div class="predictive-search__result-group">
        ${hasSuggestions ? `
          <div>
            <h4 id="predictive-search-queries" class="predictive-search__heading h6">${escapeHtml(strings.suggestions)}</h4>
            <ul id="predictive-search-results-queries-list" class="predictive-search__results-list" role="group" aria-labelledby="predictive-search-queries">
              ${suggestionItems}
            </ul>
          </div>` : ''}
        ${hasCollections ? `
          <div>
            <h4 id="predictive-search-collections" class="predictive-search__heading h6">${escapeHtml(strings.collections)}</h4>
            <ul id="predictive-search-results-collections-list" class="predictive-search__results-list" role="group" aria-labelledby="predictive-search-collections">
              ${collectionItems}
            </ul>
          </div>` : ''}
        ${hasProducts ? `
          <div>
            <h4 id="predictive-search-products" class="predictive-search__heading h6">${escapeHtml(strings.products)}</h4>
            <ul id="predictive-search-results-products-list" class="predictive-search__results-list" role="group" aria-labelledby="predictive-search-products">
              ${productItems}
            </ul>
          </div>` : ''}
      </div>`
    : '';

  return `
    <div id="predictive-search-results" role="listbox">
      ${buildDidYouMeanMarkup(data)}
      ${resultGroupMarkup}
      <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
        <button
          class="predictive-search__item predictive-search__item--term btn btn--with-icon btn--with-icon-last"
          tabindex="-1"
          role="option"
          aria-selected="false"
        >
          <span data-predictive-search-search-for-text></span>
          <svg role="presentation" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-right">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>
    </div>
    <span class="hidden" data-predictive-search-live-region-count-value></span>
  `;
}

function buildSearchResultsContent(data, searchTerm) {
  const products = data.products || [];

  return {
    markup: buildPredictiveSearchMarkup(data),
    searchForText: buildSearchForText(searchTerm),
    liveRegionText: buildLiveRegionText(data, searchTerm, products),
  };
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));

      if (this.input.value != '') {
        this.onFocus();
      }
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    //this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.closeResults(true);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);

    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.textContent;
    if (currentButtonText) {
      if (!previousTerm) {
        searchForTextElement.textContent = buildSearchForText(newTerm);
        return;
      }

      const previousTermPattern = new RegExp(escapeRegExp(previousTerm), "g");

      if ((currentButtonText.match(previousTermPattern) || []).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.textContent = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    boostInstantSearch(searchTerm, this.abortController.signal)
      .then((data) => {
        const resultsContent = buildSearchResultsContent(data, searchTerm);

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsContent;
          }
        );
        this.renderSearchResults(resultsContent);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsContent) {
    this.predictiveSearchResults.innerHTML = resultsContent.markup;

    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForTextElement) {
      searchForTextElement.textContent = resultsContent.searchForText;
    }

    const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionElement) {
      liveRegionElement.textContent = resultsContent.liveRegionText;
    }

    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden')
    } else if (this.input.value.length === 0  && !resetIsHidden) {
      this.resetButton.classList.add('hidden')
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
